import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { withRtkObjectTreePreload } from '@triedge/rtk-abstractions';
import { readRunReportsVisualizationRouteIntentFromSearch } from '@triedge/domain-core/report-generation';
import { useLocation } from 'react-router-dom';
import {
  ReportGenerationWorkspace,
  RUN_REPORTS_ASSISTANT_PATHNAME,
  useRunReportsRouteController,
  type RunReportsAssistantRouteContext,
  type ReportGenerationWorkspaceModel,
} from '@triedge/ai-assistant-ui/components';
import {
  REPORT_GENERATION_PROOF_PRESETS,
  parseReportGenerationProofPresetFromSearch,
} from '../components/ai/assistantProofPresets';
import type {
  AdmissionsAIContext,
  AdmissionsRouteAssistantContext,
} from '../components/ai/types';
import { useAppDispatch } from '../state/hooks';
import {
  readYstnjNightModePreference,
  readYstnjNightModeProviderOverride,
} from '../theme/nightMode';
import {
  closeReportGenerationExport,
  closeReportGenerationTemplateVersionHistory,
  closeReportGenerationWorkspace,
  resetReportGenerationWorkspaceView,
} from './reportGenerationWorkspaceSlice';
import {
  hasRunReportsDefinitionHandoff,
  resolveTemplateRunTitle,
  useReportGenerationWorkspaceModel,
} from './useReportGenerationWorkspaceModel';

const DEFAULT_TEMPLATE_ID = 'aging-report';
const DEFAULT_EXTRA_FIELD_IDS = ['amount_paid', 'payment_status', 'plan_name', 'academic_year'] as const;

type RunReportsRouteProps = {
  onAssistantRouteContextChange?: (next: AdmissionsRouteAssistantContext | null) => void;
};

type YstnjReportGenerationWorkspaceModel = ReportGenerationWorkspaceModel & {
  definition: NonNullable<AdmissionsAIContext['reportGenerationSidecar']>['currentDefinition'];
};

const resolveRunReportsSurfaceTheme = () => {
  const providerOverride = readYstnjNightModeProviderOverride();
  if (providerOverride !== null) {
    return providerOverride ? ('dark' as const) : ('light' as const);
  }
  if (typeof document !== 'undefined' && document.documentElement.getAttribute('data-ystnj-night-mode') === 'true') {
    return 'dark' as const;
  }
  return readYstnjNightModePreference() ? ('dark' as const) : ('light' as const);
};

const buildYstnjReportGenerationSidecarContext = (
  model: YstnjReportGenerationWorkspaceModel,
): NonNullable<AdmissionsAIContext['reportGenerationSidecar']> => {
  const definition = model.definition;
  const selectedEntityIds = new Set(definition.selectedEntities);
  const selectedFieldIds = new Set(definition.selectedFields);
  const visibleColumnIds = model.previewColumns
    .map((column) => column.fieldId)
    .filter((fieldId) => !model.workspace.hiddenColumns.includes(fieldId));
  const visibleColumnIdSet = new Set(visibleColumnIds);
  const columns = model.previewColumns
    .filter((column) => visibleColumnIdSet.has(column.fieldId))
    .slice(0, 5)
    .map((column) => ({
      fieldId: column.fieldId,
      label: column.label,
      moduleLabel: column.moduleLabel,
      entityId: column.entityId ?? null,
    }));
  const selectedEntities = model.modules
    .flatMap((module) => module.entities)
    .filter((entity) => selectedEntityIds.has(entity.id))
    .map((entity) => ({
      id: entity.id,
      label: entity.label,
      moduleLabel: entity.moduleLabel,
    }));
  const selectedFields = model.availableFields
    .filter((field) => selectedFieldIds.has(field.fieldId))
    .map((field) => ({
      id: field.fieldId,
      label: field.fieldLabel,
      entityId: field.entityId ?? null,
      moduleLabel: field.moduleLabel,
    }));
  return {
    routeContext: {
      pathname: RUN_REPORTS_ASSISTANT_PATHNAME,
      module: 'tuition',
      routeFamily: 'tuition',
      routeLabel: 'Tuition',
    },
    currentDefinition: {
      ...definition,
      pathname: RUN_REPORTS_ASSISTANT_PATHNAME,
      module: 'tuition',
      routeFamily: 'tuition',
    },
    selectedEntities,
    selectedFields,
    filters: definition.conditions,
    dateRange: definition.dateRange,
    sorts: definition.sorts,
    templates: {
      currentTemplateId: model.filterState.selectedTemplateId,
      currentTemplateSource: model.filterState.selectedTemplateSource,
      gallery: model.galleryTemplates.map((template) => ({
        id: template.id,
        name: template.name,
        description: template.description,
        fields: [...template.fields],
        entities: [...template.entities],
      })),
      saved: model.myTemplates.map((template) => ({
        id: template.id,
        name: template.name,
        visibility: template.visibility ?? null,
        isScheduled: template.isScheduled ?? null,
        fields: [...template.fields],
        entities: [...template.entities],
      })),
    },
    schedule: {
      hasActiveSchedule: model.hasActiveSchedule,
    },
    history: {
      recent: model.historyEntries.slice(0, 5).map((entry) => ({
        id: entry.id,
        templateName: entry.templateName,
        status: entry.status,
        runDate: entry.runDate,
      })),
    },
    preview: {
      sampleCap: 5,
      visibleColumnIds,
      columns,
      rows: model.previewRows.slice(0, 5).map((row) => ({
        id: row.id,
        cells: Object.fromEntries(
          columns.map((column) => {
            const value = row.cells[column.fieldId];
            return [
              column.fieldId,
              value == null || typeof value === 'string' ||
              typeof value === 'number' || typeof value === 'boolean'
                ? value ?? null
                : String(value),
            ];
          }),
        ),
      })),
      totalRowCount: model.totalRowCount,
      warnings: [...model.previewWarnings],
      blockedEntityCount: model.inaccessibleEntities.length,
      canRun: model.canPreview,
      loading: model.previewLoading,
      error: model.previewError,
    },
  };
};

function RunReportsRouteBase({
  onAssistantRouteContextChange,
}: RunReportsRouteProps = {}) {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [surfaceTheme, setSurfaceTheme] = useState(resolveRunReportsSurfaceTheme);
  const [skipDefaultInitialization] = useState(() => (
    Boolean(parseReportGenerationProofPresetFromSearch(location.search)) ||
    hasRunReportsDefinitionHandoff()
  ));
  const [routeIntent] = useState(() => (
    typeof window === 'undefined'
      ? null
      : readRunReportsVisualizationRouteIntentFromSearch(window.location.search)
  ));
  const model = useReportGenerationWorkspaceModel({
    defaultTitle: 'Tuition report',
    pathname: RUN_REPORTS_ASSISTANT_PATHNAME,
  });
  const appliedPresetRef = useRef<string | null>(null);
  const reportProofPreset = useMemo(
    () => parseReportGenerationProofPresetFromSearch(location.search),
    [location.search],
  );
  useEffect(() => {
    if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') {
      return undefined;
    }
    const observer = new MutationObserver(() => {
      setSurfaceTheme(resolveRunReportsSurfaceTheme());
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-ystnj-night-mode'],
    });
    return () => observer.disconnect();
  }, []);
  const handleRouteContextChange = useCallback(
    (next: RunReportsAssistantRouteContext | null) => {
      onAssistantRouteContextChange?.(next as AdmissionsRouteAssistantContext | null);
    },
    [onAssistantRouteContextChange],
  );
  const handleRouteUnmount = useCallback(() => {
    dispatch(closeReportGenerationWorkspace());
    dispatch(closeReportGenerationTemplateVersionHistory());
    dispatch(closeReportGenerationExport());
    dispatch(resetReportGenerationWorkspaceView());
  }, [dispatch]);
  const buildContextExtras = useCallback(
    ({ model: currentModel }: { model: ReportGenerationWorkspaceModel }) => ({
      reportGenerationSidecar: buildYstnjReportGenerationSidecarContext(
        currentModel as YstnjReportGenerationWorkspaceModel,
      ),
    }),
    [],
  );
  const controller = useRunReportsRouteController({
    model: model as unknown as ReportGenerationWorkspaceModel,
    contextNamespace: 'ystnj',
    surfaceTheme,
    defaultTemplateId: DEFAULT_TEMPLATE_ID,
    resolveTemplateTitle: resolveTemplateRunTitle,
    defaultExtraFieldIds: DEFAULT_EXTRA_FIELD_IDS,
    skipDefaultInitialization,
    buildContextExtras,
    onRouteContextChange: handleRouteContextChange,
    onUnmount: handleRouteUnmount,
    includeLocalDraftAction: true,
    routeIntent,
    visualAuthority: {
      referenceSource: 'apps/ystnj/storybook-assets/external-ui/report-generation-magic-patterns',
      routeAuthoritySource: 'run-reports',
      routeReferenceSource: 'apps/ystnj/storybook-assets/external-ui/run-reports',
    },
  });

  useEffect(() => {
    if (!reportProofPreset) {
      appliedPresetRef.current = null;
      return;
    }

    const presetSignature = [
      reportProofPreset,
      model.galleryTemplates.length,
      model.myTemplates.length,
      model.historyEntries.length,
      model.availableFields.length,
    ].join(':');
    if (appliedPresetRef.current === presetSignature) {
      return;
    }
    appliedPresetRef.current = presetSignature;

    const presetState = REPORT_GENERATION_PROOF_PRESETS[reportProofPreset];
    const matchingGalleryTemplate = presetState.reportTitle
      ? model.galleryTemplates.find((template) => template.name === presetState.reportTitle) ?? null
      : null;
    const matchingCustomTemplate = presetState.versionHistoryTemplateName
      ? model.myTemplates.find((template) => template.name === presetState.versionHistoryTemplateName) ?? null
      : presetState.reportTitle
        ? model.myTemplates.find((template) => template.name === presetState.reportTitle) ?? null
        : null;
    const matchingHistoryEntry = presetState.historyTemplateName
      ? model.historyEntries.find((entry) => entry.templateName === presetState.historyTemplateName) ?? null
      : null;

    model.actions.clearAll();
    model.actions.closeVersionHistory();
    model.actions.closeExport();
    model.actions.setContextMenuField(null);
    model.actions.setSelectedHistoryEntry(null);
    model.actions.setCollapsed(Boolean(presetState.collapsed));
    model.actions.openWorkspace(presetState.activeTab ?? 'templates');
    model.actions.setActiveTab(presetState.activeTab ?? 'templates');
    model.actions.setTemplateView(presetState.templateView ?? 'gallery');
    model.actions.setReportTitle(presetState.reportTitle ?? 'Tuition report');

    if (matchingHistoryEntry && presetState.activeTab === 'history') {
      model.actions.setSelectedHistoryEntry(matchingHistoryEntry.id);
    }

    if (matchingCustomTemplate && presetState.versionHistoryTemplateName) {
      model.actions.openVersionHistory(matchingCustomTemplate.id);
    }

    if (matchingCustomTemplate && !presetState.versionHistoryTemplateName && presetState.templateView === 'my' && presetState.runReport) {
      model.actions.applyTemplateById(matchingCustomTemplate.id, 'custom');
    } else if (matchingGalleryTemplate && presetState.runReport) {
      model.actions.applyTemplateById(matchingGalleryTemplate.id, 'gallery');
    }

    if (presetState.showExport) {
      model.actions.openExport();
    }

    if (presetState.contextMenuFieldLabel) {
      const matchingField = model.availableFields.find((field) => field.fieldLabel === presetState.contextMenuFieldLabel);
      if (matchingField) {
        model.actions.setContextMenuField(matchingField.fieldId);
      }
    }
  }, [
    model.actions,
    model.availableFields,
    model.availableFields.length,
    model.galleryTemplates,
    model.galleryTemplates.length,
    model.historyEntries,
    model.historyEntries.length,
    model.myTemplates,
    model.myTemplates.length,
    reportProofPreset,
  ]);

  return (
    <div {...controller.routeShellProps}>
      <ReportGenerationWorkspace
        model={controller.workspaceModel}
        surfaceTheme={controller.surfaceTheme}
        insightPresentation="info-strip"
      />
    </div>
  );
}

export const RunReportsRoute = withRtkObjectTreePreload<RunReportsRouteProps>(RunReportsRouteBase, {
  moduleId: 'run-reports',
  scope: 'page',
  resourceKeys: ['workspace'],
});
